import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export type SupplierType = 'workshop' | 'parts_store' | 'tire_shop' | 'other';

export interface Supplier {
  id: string;
  name: string;
  type: SupplierType;
  cnpj: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  active: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SuppliersListResponse {
  data: Supplier[];
  meta: { page: number; limit: number; total: number };
}

export const SUPPLIER_TYPE_LABELS: Record<SupplierType, string> = {
  workshop: 'Oficina',
  parts_store: 'Loja de peças',
  tire_shop: 'Borracharia',
  other: 'Outro',
};

interface ListParams {
  page?: number;
  limit?: number;
  q?: string;
  type?: string;
  active?: 'true' | 'false';
}

const KEY = {
  all: ['suppliers'] as const,
  list: (p: ListParams) => ['suppliers', 'list', p] as const,
  one: (id: string) => ['suppliers', 'one', id] as const,
};

export function useSuppliersList(params: ListParams = {}) {
  return useQuery<SuppliersListResponse>({
    queryKey: KEY.list(params),
    queryFn: async () => {
      const { data } = await api.get<SuppliersListResponse>('/suppliers', {
        params,
      });
      return data;
    },
  });
}

export interface SupplierFormInput {
  name: string;
  type: SupplierType;
  cnpj?: string;
  phone?: string;
  email?: string;
  address?: string;
  city?: string;
  state?: string;
  active?: boolean;
  notes?: string;
}

export function useCreateSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SupplierFormInput) => {
      const { data } = await api.post<{ data: Supplier }>('/suppliers', input);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY.all }),
  });
}

export function useUpdateSupplier(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<SupplierFormInput>) => {
      const { data } = await api.patch<{ data: Supplier }>(`/suppliers/${id}`, input);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY.all }),
  });
}

export function useDeleteSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/suppliers/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY.all }),
  });
}
